import React from 'react';

/**
 * Icon — the inspection app's full glyph set as inline SVG.
 * Lucide-style geometry: 24×24 grid, 2px stroke, round caps/joins.
 * Stroke uses currentColor so the icon inherits text color.
 */

export type IconName =
  | 'check' | 'check-circle' | 'triangle-alert' | 'lock' | 'ban' | 'clock' | 'circle-dot'
  | 'pencil' | 'wifi-off' | 'upload' | 'download' | 'refresh'
  | 'chevron-down' | 'chevron-up' | 'chevron-right'
  | 'building' | 'home' | 'flame' | 'plus'
  | 'clipboard' | 'users' | 'receipt' | 'settings' | 'help' | 'send' | 'calendar' | 'arrow-right' | 'phone' | 'mail'
  | 'trash' | 'print' | 'x' | 'map';

export interface IconProps extends React.SVGProps<SVGSVGElement> {
  /** Which glyph to render. */
  name: IconName;
  /** Pixel size (width = height). Default 24. */
  size?: number;
  /** Stroke width. Default 2. */
  strokeWidth?: number;
  /** Override stroke color (defaults to currentColor). */
  color?: string;
  /** Accessible label; when set the icon is exposed to AT. */
  title?: string;
}

const PATHS: Record<IconName, React.ReactNode> = {
  // status
  'check':         <path d="M20 6 9 17l-5-5" />,
  // 045 (§15): pin lokalizacji — mapka punktu budynku w „Dodaj budynek"
  'map':           <><path d="M20 10c0 6-8 12-8 12S4 16 4 10a8 8 0 1 1 16 0Z" /><circle cx="12" cy="10" r="3" /></>,
  'check-circle':  <><circle cx="12" cy="12" r="9" /><path d="m8.5 12 2.5 2.5 4.5-5" /></>,
  'triangle-alert':<><path d="M21.73 18 13.73 4a2 2 0 0 0-3.46 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" /><path d="M12 9v4" /><path d="M12 17h.01" /></>,
  'lock':          <><rect x="3.5" y="11" width="17" height="10" rx="2" /><path d="M7.5 11V7a4.5 4.5 0 0 1 9 0v4" /></>,
  'ban':           <><circle cx="12" cy="12" r="9" /><path d="m5.6 5.6 12.8 12.8" /></>,
  'clock':         <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3.5 2" /></>,
  'circle-dot':    <><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="3.5" fill="currentColor" stroke="none" /></>,
  // editing / state
  'pencil':        <><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" /><path d="m14.5 5.5 3 3" /></>,
  // sync
  'wifi-off':      <><path d="M2 3l18 18" /><path d="M5 12.6a9 9 0 0 1 4-2.2" /><path d="M8.6 16.1a4.5 4.5 0 0 1 5.4-.7" /><path d="M11 5.1A14 14 0 0 1 21 9" /><path d="M12 20h.01" /></>,
  'upload':        <><path d="M12 14V4" /><path d="m8 8 4-4 4 4" /><path d="M5 14v4a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-4" /></>,
  'download':      <><path d="M12 4v10" /><path d="m8 10 4 4 4-4" /><path d="M5 14v4a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-4" /></>,
  'refresh':       <><path d="M3 12a9 9 0 0 1 15.5-6.3L21 8" /><path d="M21 4v4h-4" /><path d="M21 12a9 9 0 0 1-15.5 6.3L3 16" /><path d="M3 20v-4h4" /></>,
  // chevrons
  'chevron-down':  <path d="m6 9 6 6 6-6" />,
  'chevron-up':    <path d="m6 15 6-6 6 6" />,
  'chevron-right': <path d="m9 18 6-6-6-6" />,
  // places
  'building':      <><path d="M6 21V5a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v16" /><path d="M3 21h18" /><path d="M9.5 8h0M14.5 8h0M9.5 12h0M14.5 12h0" /><path d="M10.5 21v-3a1.5 1.5 0 0 1 3 0v3" /></>,
  'home':          <><path d="m3 10 9-7 9 7v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><path d="M9 21v-8h6v8" /></>,
  'flame':         <path d="M12 3c1 3 4 4.5 4 8a4 4 0 0 1-8 0c0-1.2.5-2.2 1-3 .3 1 .8 1.5 1.5 1.7C10 8 11 5.5 12 3Z" />,
  'plus':          <><path d="M12 5v14" /><path d="M5 12h14" /></>,
  // admin nav + tables
  'clipboard':     <><rect x="8" y="3" width="8" height="4" rx="1" /><path d="M16 5h2a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h2" /><path d="M9 12h6" /><path d="M9 16h4" /></>,
  'users':         <><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></>,
  'receipt':       <><path d="M5 3h14a1 1 0 0 1 1 1v17l-3-2-3 2-3-2-3 2V4a1 1 0 0 1 1-1Z" /><path d="M8 8h8" /><path d="M8 12h8" /><path d="M8 16h5" /></>,
  'settings':      <><circle cx="12" cy="12" r="3" /><path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2Z" /></>,
  'help':          <><circle cx="12" cy="12" r="9" /><path d="M9.2 9.2a3 3 0 0 1 5.6 1c0 2-3 2.5-3 4" /><path d="M12 17h.01" /></>,
  'send':          <><path d="M22 2 11 13" /><path d="M22 2 15 22l-4-9-9-4 20-7Z" /></>,
  'calendar':      <><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4" /><path d="M8 2v4" /><path d="M3 10h18" /></>,
  'arrow-right':   <><path d="M5 12h14" /><path d="m13 5 7 7-7 7" /></>,
  'phone':         <path d="M13.8 16.6a1 1 0 0 0 1.2-.3l.4-.5a2 2 0 0 1 1.6-.8h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2A18 18 0 0 1 2 4a2 2 0 0 1 2-2h3a2 2 0 0 1 2 2v3a2 2 0 0 1-.8 1.6l-.5.4a1 1 0 0 0-.3 1.2 14 14 0 0 0 6.4 6.4Z" />,
  'mail':          <><rect x="2" y="4" width="20" height="16" rx="2" /><path d="m2 7 10 6 10-6" /></>,
  // actions
  'trash':         <><path d="M3 6h18" /><path d="M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" /><path d="M10 11v6" /><path d="M14 11v6" /></>,
  'print':         <><path d="M6 9V3h12v6" /><path d="M6 18H5a2 2 0 0 1-2-2v-4a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2h-1" /><rect x="6" y="14" width="12" height="7" rx="1" /></>,
  'x':             <><path d="M18 6 6 18" /><path d="m6 6 12 12" /></>,
};

export function Icon({ name, size = 24, strokeWidth = 2, color, style, title, ...rest }: IconProps) {
  const glyph = PATHS[name];
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color || 'currentColor'}
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      role={title ? 'img' : 'presentation'}
      aria-hidden={title ? undefined : true}
      aria-label={title}
      style={{ display: 'block', flex: 'none', ...style }}
      {...rest}
    >
      {title ? <title>{title}</title> : null}
      {glyph || null}
    </svg>
  );
}
